import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Filter, Store, ListFilter, Check, X, AlertTriangle, Trash2, Calendar, FileText, ArrowDownUp } from 'lucide-react';
import { TrocoFaseBadge, TrocoOperacaoBadge, OPERACAO, rotuloOperacao, rotuloFase } from './trocoStatus';
import { MES_MINIMO, mesAtual } from './trocoFiltros';
import { Moeda, brl, VERDE, VERDE_CLARO } from './trocoFormato';
import useOrdenacaoTabela from '../useOrdenacaoTabela';
// Vídeo do procedimento embutido na tela (componente compartilhado).
// ⚠️ O `apiBase` é o proxy do MÓDULO dono do vídeo — cada módulo tem o seu.
import VideoProcedimento from '../../VideoProcedimento';

// Proxy de vídeo do módulo Financeiro (catálogo em dtbVideoProcedimento).
const API_VIDEOS = '/api/financeiro/videos';

// ============================================================================
// CONTROLE — o relatório de operações do Troco
// ============================================================================
// Reescrita de 2026-07-30 (Fase 2 da reformulação, demanda Julia). A tela deixou
// de listar linhas da dtbTroco e passou a listar OPERAÇÕES — uma linha por
// evento — no formato do "Relatório de troco" que a operação já usa impresso:
//
//   SALDO INICIAL
//   Lançamento de Troco     | data | hora | nº lacre |          | + valor
//   Depósito de Troco       | data | hora |          | nº banana| − valor
//   Devolução ao Escritório | data | hora |          |          | − valor
//   SALDO FINAL
//
// ⚠️ O QUE APARECE (ajustado em 2026-07-30, depois do preview com a Julia):
//   LANÇAMENTO de troco → em QUALQUER status, inclusive Pendente. É o que o
//     gerente precisa ver: o troco que chegou e ele ainda não confirmou. A linha
//     em aberto vem ESMAECIDA — existe, mas ainda não conta no saldo.
//   OPERAÇÃO DE CAIXA (depósito/devolução/correção) → só CONFIRMADA pelo
//     Financeiro. Depósito confirmado e não confirmado são linhas visualmente
//     idênticas e só uma mexe no dinheiro — mostrar as duas confundia gerente e
//     Financeiro, que é o oposto do que esta tela existe para fazer.
//
// ⚠️ Nada se perde: o troco depositado e ainda não confirmado continua visível
// na PRÓPRIA linha do lançamento, cuja fase mostra "Depósito". Quem precisa AGIR
// sobre depósito pendente usa a aba Conferência — esta aqui é de monitoramento.
//
// ⚠️ SEM O CARIMBO DA CONFERÊNCIA PDV na coluna Nº Banana (removido no mesmo
// preview): "Conferido"/"Sem lastro" ao lado do número virava ruído para quem só
// quer acompanhar o dinheiro, e o "Sem lastro" assustava sem haver o que fazer a
// respeito aqui. Esse veredito é assunto da aba Conferência.
//
// ⚠️ ORDENAÇÃO POR COLUNA (2026-07-30, a pedido da Julia). A ordem PADRÃO segue
// sendo a cronológica — é ela que faz a lista contar a história do dinheiro —
// mas toda coluna é clicável, com a seta `⇅` indicando isso, e um atalho devolve
// a ordem cronológica a qualquer momento.
//
// ⚠️ Ordenar NÃO estraga os bookends: "Saldo inicial/final do período" são
// TOTAIS DO PERÍODO calculados no ledger pelo servidor, não um saldo acumulado
// linha a linha. A ordem das linhas não os afeta — por isso a tela pode ser
// reordenada à vontade sem os dois saldos deixarem de fazer sentido.
//
// A ordem, os sinais e os saldos vêm prontos de GET /api/troco/controle — a
// regra mora no motor puro `montarControle` (server/financeiro/troco-utils.js),
// testado. A tela não recalcula dinheiro.

const TEAL = '#0f766e';         // cor de identidade do módulo Financeiro
const TEAL_CLARO = '#e0f2f1';

// Tipo de cada coluna ordenável (ver useOrdenacaoTabela). Fora do componente
// para a identidade ser estável e o useMemo do hook não reordenar a cada render.
//
// ⚠️ `data` e `hora` são TEXTO de propósito: chegam em ISO ('2026-06-03',
// '17:31:07'), formato que já ordena cronologicamente como string. O tipo 'data'
// do hook espera dd/mm/aaaa e não serviria aqui.
//
// ⚠️ `_operacao` e `_situacao` são campos DERIVADOS (o rótulo de tela). Ordenar
// pelo código do banco daria uma ordem que não bate com o que o olho lê — em
// `status`, por exemplo, 'Aprovado' viria antes de 'Depositado', mas na tela
// aparecem "Recebido pela Loja" e "Depósito".
const TIPOS_ORDENACAO = {
    _operacao: 'texto',
    loja: 'texto',
    data: 'texto',
    hora: 'texto',
    numeroLacre: 'texto',
    numeroBanana: 'texto',
    valor: 'numero',
    confirmadoPor: 'texto',
    _situacao: 'texto',
};

// Cabeçalho clicável: cursor de mão e sem seleção de texto ao clicar.
const thOrdenavel = { cursor: 'pointer', userSelect: 'none' };

// Filtro por tipo de operação — as quatro do relatório.
const FILTROS_OPERACAO = [
    { valor: '', rotulo: 'Todas as operações' },
    { valor: OPERACAO.LANCAMENTO, rotulo: rotuloOperacao(OPERACAO.LANCAMENTO) },
    { valor: OPERACAO.DEPOSITO, rotulo: rotuloOperacao(OPERACAO.DEPOSITO) },
    { valor: OPERACAO.DEVOLUCAO, rotulo: rotuloOperacao(OPERACAO.DEVOLUCAO) },
    { valor: OPERACAO.CORRECAO, rotulo: rotuloOperacao(OPERACAO.CORRECAO) },
];

const FinanceiroTrocoControle = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Gerente Plantão tem o MESMO acesso do Gerente: preso à própria loja e pode confirmar.
    const isGerente = user?.perfil === 'Gerente' || user?.perfil === 'Gerente Plantao';
    // ⚠️ `Operacional` (28/09/2026): acesso total ao Financeiro (nível Admin).
    const podeConfirmar = ['Admin', 'Financeiro', 'Gerente', 'Gerente Plantao', 'Gerente Adm', 'Operacional'].includes(user?.perfil);
    // Só Admin (e o Operacional, nível Admin) pode EXCLUIR lançamentos (ação destrutiva e definitiva).
    const isAdmin = user?.perfil === 'Admin' || user?.perfil === 'Operacional';

    const [lojas, setLojas] = useState([]);
    const [relatorio, setRelatorio] = useState(null);   // { operacoes, saldoInicial, saldoFinal, totais }
    const [saldoLoja, setSaldoLoja] = useState(null);   // só para o aviso de teto estourado
    const [isCarregando, setIsCarregando] = useState(false);

    // Filtros
    const [lojaSelecionada, setLojaSelecionada] = useState(isGerente && user.loja ? user.loja : '');
    const [operacaoSelecionada, setOperacaoSelecionada] = useState('');
    // Competência (YYYY-MM) pela data do fato. Abre no mês corrente; o "limpar"
    // ao lado do seletor traz o histórico todo.
    const [mes, setMes] = useState(mesAtual);

    // Modais
    const [modalConfirmar, setModalConfirmar] = useState(null);
    const [isConfirmando, setIsConfirmando] = useState(false);
    const [itemParaExcluir, setItemParaExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);

    const auth = useCallback(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        return { headers: { Authorization: `Bearer ${token}` } };
    }, []);

    // Lojas (uma vez)
    useEffect(() => {
        fetch('/api/troco/lojas', auth())
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setLojas(d); })
            .catch(err => console.error('Erro ao carregar lojas:', err));
    }, [auth]);

    // O relatório inteiro vem do backend já ordenado, com sinal e com os saldos
    // calculados. Recarrega quando qualquer filtro muda.
    const carregarRelatorio = useCallback(() => {
        setIsCarregando(true);
        const params = new URLSearchParams();
        if (lojaSelecionada) params.set('loja', lojaSelecionada);
        if (mes) params.set('mes', mes);

        fetch(`/api/troco/controle?${params}`, auth())
            .then(r => r.json())
            .then(d => { if (Array.isArray(d?.operacoes)) setRelatorio(d); })
            .catch(err => console.error('Erro ao carregar o controle:', err))
            .finally(() => setIsCarregando(false));
    }, [lojaSelecionada, mes, auth]);

    useEffect(() => { carregarRelatorio(); }, [carregarRelatorio]);

    // Saldo da loja — só para o aviso de teto estourado (o número do disponível
    // é da tela de Envio, onde ele é acionável).
    useEffect(() => {
        if (!lojaSelecionada) { setSaldoLoja(null); return; }
        fetch(`/api/troco/saldo-loja/${encodeURIComponent(lojaSelecionada)}`, auth())
            .then(r => r.json())
            .then(d => { if (d && d.saldo !== undefined) setSaldoLoja(d); })
            .catch(err => console.error('Erro ao buscar saldo da loja:', err));
    }, [lojaSelecionada, relatorio, auth]);

    // Confirmação de recebimento (etapa 2) — a única AÇÃO que mora nesta tela.
    // As etapas 3 e 4 (depósito e confirmação) vivem na aba Conferência.
    const handleConfirmarRecebimento = async () => {
        if (!modalConfirmar) return;
        setIsConfirmando(true);
        try {
            const res = await fetch(`/api/troco/confirmar/${modalConfirmar.id}`, { method: 'PUT', ...auth() });
            if (res.ok) {
                setModalConfirmar(null);
                carregarRelatorio();       // relê: o saldo e a fase mudaram
            } else {
                const d = await res.json().catch(() => ({}));
                alert(d.error || 'Erro ao confirmar recebimento.');
            }
        } catch {
            alert('Erro de conexão com o servidor.');
        } finally {
            setIsConfirmando(false);
        }
    };

    const confirmarExclusao = async () => {
        if (!itemParaExcluir) return;
        setExcluindo(true);
        try {
            // ⚠️ Acerto (devolução/correção) vive na dtbTrocoMovimento e é
            // identificado por ID NEGATIVO na API — ver DELETE /api/troco/:id.
            const id = itemParaExcluir.origem === 'movimento' ? -itemParaExcluir.id : itemParaExcluir.id;
            const res = await fetch(`/api/troco/${id}`, { method: 'DELETE', ...auth() });
            if (res.ok) {
                setItemParaExcluir(null);
                carregarRelatorio();
            } else {
                const d = await res.json().catch(() => ({}));
                alert(d.error || 'Erro ao excluir.');
            }
        } catch {
            alert('Erro de conexão com o servidor.');
        } finally {
            setExcluindo(false);
        }
    };

    // Filtro de tipo é local (o recorte pesado — loja e mês — é do servidor).
    // Aqui também nascem os campos derivados que a ordenação usa (ver TIPOS_ORDENACAO).
    const operacoes = useMemo(() => (relatorio?.operacoes || [])
        .filter(o => !operacaoSelecionada || o.operacao === operacaoSelecionada)
        .map(o => ({ ...o, _operacao: rotuloOperacao(o.operacao), _situacao: rotuloFase(o.status) })),
        [relatorio, operacaoSelecionada]);

    // Ordenação clicável pelo cabeçalho. Sem clique nenhum, `dadosOrdenados` é a
    // lista como o servidor entregou — ou seja, a ordem cronológica do relatório.
    const { dadosOrdenados, ordenar, indicador, ordenacao, limparOrdenacao } = useOrdenacaoTabela(operacoes, TIPOS_ORDENACAO);

    // ⚠️ SEM PAGINAÇÃO, DE PROPÓSITO (corrigido em 2026-07-30).
    //   A tela paginava de 10 em 10 e os bookends (Saldo inicial/final) ficavam
    //   DENTRO do corpo paginado. Resultado real relatado pela Julia: em VSH sem
    //   filtro de mês, o lançamento de 28/07 (lacre 5887, R$ 980,00) era a 13ª de
    //   13 operações — caía na página 2 — e o "Saldo final" aparecia logo abaixo
    //   da 10ª linha, parecendo o total daquelas 10 e sem fechar.
    //
    //   O saldo estava CERTO (é o total do período, vindo do ledger); errada era
    //   a apresentação. E não há remendo bom: um relatório com totais não pode
    //   esconder linhas — qualquer página parcial faz os bookends mentirem.
    //   Relatório de papel não pagina de 10 em 10; este também não.
    //
    //   O volume é contido pelo filtro de mês, que já abre preenchido.

    const temBookends = relatorio?.saldoInicial !== null && relatorio?.saldoInicial !== undefined;
    const emAberto = operacoes.filter(o => !o.contaNoSaldo).length;

    const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '15px', outline: 'none' };
    const th = { padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontWeight: '700' };
    const td = { padding: '14px 16px', fontSize: '13px', color: '#475569' };
    // Linha de saldo (bookend) — o mesmo peso visual do relatório impresso.
    const tdSaldo = { padding: '14px 16px', fontSize: '13px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.02em' };

    // Nº de colunas da tabela, para o colSpan dos bookends e do vazio.
    const colunas = 8 + (lojaSelecionada ? 0 : 1);

    return (
        <div>
            {/* O "como fazer" no ALTO da tela, antes dos filtros — esta é a tela em
                que a LOJA confirma o recebimento do troco (a única ação que mora
                aqui), e é esse o procedimento do vídeo. Some sozinho se o vídeo não
                estiver no ar (o componente não desenha nada com 404). */}
            <div style={{ marginBottom: '16px' }}>
                <VideoProcedimento
                    apiBase={API_VIDEOS}
                    chave="recebimento-troco"
                    cor={TEAL}
                />
            </div>

            {/* ---------------------------------------------------------------- FILTROS */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px', marginBottom: '24px', backgroundColor: '#fff', padding: '16px 24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                    <Filter size={18} /> <span style={{ fontWeight: '600', fontSize: '14px' }}>Filtros:</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Store size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select
                        value={lojaSelecionada}
                        onChange={(e) => setLojaSelecionada(e.target.value)}
                        style={{ ...inputStyle, paddingLeft: '36px', backgroundColor: isGerente ? '#f1f5f9' : '#fff' }}
                        disabled={isGerente}
                    >
                        <option value="">Todas as Lojas</option>
                        {isGerente && !lojas.find(l => l.NOME_LOJA === user.loja) && (
                            <option value={user.loja}>{user.loja}</option>
                        )}
                        {lojas.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <ListFilter size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select value={operacaoSelecionada} onChange={(e) => setOperacaoSelecionada(e.target.value)} style={{ ...inputStyle, paddingLeft: '36px' }}>
                        {FILTROS_OPERACAO.map(f => <option key={f.valor} value={f.valor}>{f.rotulo}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} color="#94a3b8" />
                    <input type="month" min={MES_MINIMO} value={mes} onChange={(e) => setMes(e.target.value)} style={inputStyle} />
                    {mes && (
                        <button onClick={() => setMes('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>limpar</button>
                    )}
                </div>
            </div>

            {/* Aviso de loja acima do teto — a ação é do gerente, então o texto diz
                o que fazer, não só que estourou. */}
            {lojaSelecionada && saldoLoja?.excedido && (
                <div style={{ display: 'flex', gap: '10px', padding: '14px 16px', borderRadius: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginBottom: '24px' }}>
                    <AlertTriangle size={18} color="#b91c1c" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div style={{ fontSize: '13px', color: '#7f1d1d', lineHeight: 1.5 }}>
                        <strong>{lojaSelecionada} está acima do teto de {brl(saldoLoja.teto)} de numerário.</strong>{' '}
                        O gerente precisa <strong>depositar o dinheiro</strong> e o Financeiro confirmar o depósito
                        para liberar novos envios de troco.
                        {!saldoLoja.vigente && ' O bloqueio passa a valer em 01/08/2026.'}
                    </div>
                </div>
            )}

            {/* ------------------------------------------------------- O RELATÓRIO */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '9px' }}>
                        <FileText size={19} color={TEAL} />
                        Relatório de Troco
                        {lojaSelecionada && <span style={{ color: TEAL }}>· {lojaSelecionada}</span>}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {/* A ordem cronológica é a "natural" deste relatório — depois de
                            clicar num cabeçalho, este atalho é o caminho de volta. */}
                        {ordenacao && (
                            <button
                                onClick={limparOrdenacao}
                                title="Voltar à ordem cronológica do relatório"
                                style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '12.5px', fontWeight: '600', padding: 0 }}
                            >
                                <ArrowDownUp size={13} /> ordem cronológica
                            </button>
                        )}
                        {isCarregando && <span style={{ fontSize: '12px', color: '#64748b' }}>Atualizando…</span>}
                    </div>
                </div>

                {/* Explica a diferença entre "somei as linhas" e o saldo final. Como
                    a lista agora só traz operação de caixa CONFIRMADA, o único caso
                    que sobra é o troco que a loja ainda não confirmou ter recebido —
                    então o aviso fala exatamente disso, em vez de genericamente. */}
                {emAberto > 0 && (
                    <div style={{ display: 'flex', gap: '9px', padding: '12px 20px', backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                        <AlertTriangle size={15} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                        <span style={{ fontSize: '12.5px', color: '#92400e', lineHeight: 1.5 }}>
                            <strong>{emAberto} {emAberto === 1 ? 'troco aguarda' : 'trocos aguardam'} confirmação de recebimento pela loja.</strong>{' '}
                            {emAberto === 1 ? 'Ele aparece esmaecido' : 'Eles aparecem esmaecidos'} e <strong>ainda não {emAberto === 1 ? 'entra' : 'entram'} no saldo</strong> —
                            por isso a soma das linhas pode não fechar com o saldo final.
                        </span>
                    </div>
                )}

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '1080px' }}>
                        <thead style={{ backgroundColor: '#f8fafc', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                            <tr>
                                <th onClick={() => ordenar('_operacao')} style={{ ...th, ...thOrdenavel }}>Tipo de Operação{indicador('_operacao')}</th>
                                {!lojaSelecionada && <th onClick={() => ordenar('loja')} style={{ ...th, ...thOrdenavel }}>Loja{indicador('loja')}</th>}
                                <th onClick={() => ordenar('data')} style={{ ...th, ...thOrdenavel }}>Data{indicador('data')}</th>
                                <th onClick={() => ordenar('hora')} style={{ ...th, ...thOrdenavel }}>Hora{indicador('hora')}</th>
                                <th onClick={() => ordenar('numeroLacre')} style={{ ...th, ...thOrdenavel }}>Nº Lacre{indicador('numeroLacre')}</th>
                                <th onClick={() => ordenar('numeroBanana')} style={{ ...th, ...thOrdenavel }}>Nº Banana{indicador('numeroBanana')}</th>
                                <th onClick={() => ordenar('valor')} style={{ ...th, ...thOrdenavel, textAlign: 'right' }}>Valor{indicador('valor')}</th>
                                <th onClick={() => ordenar('confirmadoPor')} style={{ ...th, ...thOrdenavel }}>Confirmado por{indicador('confirmadoPor')}</th>
                                <th onClick={() => ordenar('_situacao')} style={{ ...th, ...thOrdenavel }}>Situação{indicador('_situacao')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* SALDO INICIAL — bookend do período, como no relatório impresso.
                                Só existe com uma loja escolhida: somar a semente de treze
                                lojas não produz um saldo que signifique alguma coisa. */}
                            {temBookends && (
                                <tr style={{ backgroundColor: TEAL_CLARO, borderBottom: `2px solid ${TEAL}` }}>
                                    <td colSpan={colunas - 3} style={tdSaldo}>Saldo inicial do período</td>
                                    <td style={{ ...tdSaldo, textAlign: 'right', fontSize: '15px' }}>
                                        <Moeda valor={relatorio.saldoInicial} cor={TEAL} />
                                    </td>
                                    <td colSpan="2" />
                                </tr>
                            )}

                            {operacoes.length === 0 && !isCarregando ? (
                                <tr>
                                    <td colSpan={colunas} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                        Nenhuma operação encontrada para este filtro.
                                    </td>
                                </tr>
                            ) : dadosOrdenados.map((o) => {
                                return (
                                    <tr
                                        key={`${o.origem}-${o.id}`}
                                        style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            // Em aberto: esmaecida. A operação existe, mas ainda
                                            // não conta no saldo — e a tela precisa dizer isso
                                            // sem esconder a linha.
                                            opacity: o.contaNoSaldo ? 1 : 0.55,
                                            backgroundColor: o.contaNoSaldo ? '#fff' : '#fcfcfd',
                                        }}
                                    >
                                        <td style={{ padding: '14px 16px' }}>
                                            <TrocoOperacaoBadge operacao={o.operacao} />
                                            {/* O motivo só existe no acerto e é a única coisa que
                                                explica a linha daqui a seis meses. */}
                                            {o.motivo && (
                                                <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '5px', maxWidth: '260px', lineHeight: 1.4 }}>
                                                    {o.motivo}
                                                </div>
                                            )}
                                        </td>
                                        {!lojaSelecionada && (
                                            <td style={{ ...td, fontWeight: '700', color: '#0f172a' }}>{o.loja || '—'}</td>
                                        )}
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                            {o.data ? o.data.split('-').reverse().join('/') : '—'}
                                        </td>
                                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: '#94a3b8' }}>{o.hora || '—'}</td>
                                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{o.numeroLacre || '—'}</td>
                                        {/* Só o número. O veredito da conferência com o PDV
                                            ("Conferido"/"Sem lastro") saiu daqui — é assunto da
                                            aba Conferência, não desta tela de acompanhamento. */}
                                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                                            {o.numeroBanana || '—'}
                                        </td>
                                        {/* O sinal vem do backend (positivo no lançamento, negativo
                                            no depósito/devolução) — a tela não decide sinal. */}
                                        <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '700', textAlign: 'right' }}>
                                            <Moeda valor={o.valor} sinalPositivo />
                                        </td>
                                        <td style={{ ...td }}>{o.confirmadoPor || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <TrocoFaseBadge status={o.status} />
                                                {/* Etapa 2: a loja confirma que o troco chegou. */}
                                                {o.origem === 'troco' && o.status === 'Pendente' && podeConfirmar && (
                                                    <button
                                                        onClick={() => setModalConfirmar({ id: o.id, loja: o.loja, valor: o.valor, numeroLacre: o.numeroLacre, data: o.data })}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', backgroundColor: VERDE, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer' }}
                                                    >
                                                        <Check size={13} /> Confirmar
                                                    </button>
                                                )}
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => setItemParaExcluir(o)}
                                                        title="Excluir"
                                                        style={{ display: 'flex', alignItems: 'center', padding: '5px 8px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer' }}
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* SALDO FINAL — o outro bookend. Segue o LEDGER (só o que foi
                                confirmado conta), e não a soma das linhas visíveis: é por
                                isso que existe o aviso de operações em aberto acima. */}
                            {temBookends && operacoes.length > 0 && (
                                <tr style={{ backgroundColor: TEAL_CLARO, borderTop: `2px solid ${TEAL}` }}>
                                    {/* O contador deixa explícito que o saldo fecha sobre a
                                        lista TODA — foi o que faltou quando a tela paginava. */}
                                    <td colSpan={colunas - 3} style={tdSaldo}>
                                        Saldo final do período
                                        <span style={{ marginLeft: '10px', fontWeight: '600', textTransform: 'none', color: '#64748b', fontSize: '12px', letterSpacing: 0 }}>
                                            {dadosOrdenados.length} operação(ões)
                                        </span>
                                    </td>
                                    <td style={{ ...tdSaldo, textAlign: 'right', fontSize: '15px' }}>
                                        <Moeda valor={relatorio.saldoFinal} cor={TEAL} />
                                    </td>
                                    <td colSpan="2" />
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* -------------------------------------------- MODAL: confirmar recebimento */}
            {modalConfirmar && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: '440px', margin: '16px', overflow: 'hidden' }}>
                        <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '10px' }}>
                                    <Check size={22} color={VERDE} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>Confirmar Recebimento</h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' }}>Verifique os dados antes de confirmar</p>
                                </div>
                            </div>
                            <button onClick={() => setModalConfirmar(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <div style={{ padding: '24px' }}>
                            <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '20px' }}>
                                {[
                                    { label: 'Loja', value: modalConfirmar.loja || lojaSelecionada || '—' },
                                    { label: 'Data', value: modalConfirmar.data ? modalConfirmar.data.split('-').reverse().join('/') : '—' },
                                    { label: 'Nº do Lacre', value: modalConfirmar.numeroLacre || '—' },
                                    { label: 'Valor', value: brl(modalConfirmar.valor), destaque: true },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: i < 3 ? '1px solid #e2e8f0' : 'none' }}>
                                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>{item.label}</span>
                                        <span style={{ fontSize: item.destaque ? '16px' : '13px', fontWeight: item.destaque ? '800' : '600', color: item.destaque ? VERDE : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{item.value}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px', backgroundColor: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a', marginBottom: '24px' }}>
                                <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <p style={{ margin: 0, fontSize: '12px', color: '#92400e', lineHeight: '1.5' }}>
                                    Ao confirmar, você atesta que o lacre foi <strong>recebido fisicamente</strong> e o valor está correto. Esta ação não pode ser desfeita.
                                </p>
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button onClick={() => setModalConfirmar(null)} disabled={isConfirmando}
                                    style={{ flex: 1, padding: '12px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                                    Cancelar
                                </button>
                                <button onClick={handleConfirmarRecebimento} disabled={isConfirmando}
                                    style={{ flex: 1, padding: '12px', backgroundColor: isConfirmando ? VERDE_CLARO : VERDE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: isConfirmando ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    {isConfirmando ? 'Confirmando…' : <><Check size={16} /> Confirmar Recebimento</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------------------------------------------- MODAL: excluir (Admin) */}
            {itemParaExcluir && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '14px', width: '90%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Trash2 size={18} color="#dc2626" />
                            </div>
                            <h3 style={{ margin: 0, color: '#b91c1c', fontSize: '18px', fontWeight: '700' }}>
                                Excluir {itemParaExcluir.origem === 'movimento' ? 'operação' : 'lançamento'}
                            </h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                            Esta ação é <strong>definitiva</strong> e não pode ser desfeita. Confirme os dados:
                        </p>
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '14px', color: '#334155', display: 'grid', gap: '4px' }}>
                            <div><strong>Operação:</strong> {rotuloOperacao(itemParaExcluir.operacao)}</div>
                            <div><strong>Loja:</strong> {itemParaExcluir.loja || lojaSelecionada || '—'}</div>
                            <div><strong>Data:</strong> {itemParaExcluir.data ? itemParaExcluir.data.split('-').reverse().join('/') : '—'}</div>
                            <div><strong>Valor:</strong> {brl(itemParaExcluir.valor)}</div>
                        </div>
                        {/* Um lançamento já quitado por um depósito é recusado pelo servidor
                            (FK RESTRICT) — avisar antes evita o clique que vai falhar. */}
                        {itemParaExcluir.origem === 'troco' && itemParaExcluir.status !== 'Pendente' && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px' }}>
                                <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span style={{ fontSize: '13px', color: '#92400e' }}>
                                    Este troco já saiu do estado <strong>Pendente</strong>. Se ele já foi quitado por um depósito,
                                    o servidor vai recusar a exclusão — desfaça o depósito primeiro.
                                </span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => setItemParaExcluir(null)} disabled={excluindo}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                                Cancelar
                            </button>
                            <button onClick={confirmarExclusao} disabled={excluindo}
                                style={{ padding: '10px 18px', backgroundColor: excluindo ? '#fca5a5' : '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: excluindo ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Trash2 size={15} /> {excluindo ? 'Excluindo…' : 'Excluir definitivamente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceiroTrocoControle;
